// Background music: "A Digital Christmas Tale" (79.9 s), played with Web Audio so the menu loop is
// gapless. During the journey the weather in the film's own sound comes first: the song starts at
// MUSIC_START, fades in underneath, rises as the weather dies away and reaches its normal level on
// arrival.
//
// Timing: the song is scheduled on the AudioContext clock. Whenever the film stops (pause, a stall,
// the device notice) the page suspends the context, which freezes the song and its fades with it,
// so music and film stay together. All scheduling is synchronous, so there is only ever one source.

const BASE = "./media/";

/** Music level relative to the film's own sound. The film sits at -26.5 LUFS, the track at -15.1. */
export const LEVEL = {
  /** under the weather, the first part of the journey */
  under: 0.09,
  /** when the camera is inside the cabin */
  inside: 0.26,
  /** in the menu (arrival and after) */
  normal: 0.45,
};

/** The song starts from its beginning at this film time (owner: "around 4 s"). */
export const MUSIC_START = 4;

/** Fade-in curve over the journey, in seconds of film time. */
const JOURNEY_CURVE: Array<[number, number]> = [
  [0, 0],
  [MUSIC_START, 0],
  [MUSIC_START + 3, LEVEL.under],
  [17, LEVEL.under], // still outside: forest, road, cabin, door
  [24, LEVEL.inside], // the room, the desk
];

export function musicUrl(): string {
  const a = document.createElement("audio");
  return a.canPlayType('audio/webm; codecs="opus"') ? BASE + "music.opus.webm" : BASE + "music.m4a";
}

type Want = { mode: "journey" } | { mode: "menu"; seconds: number };

export class Music {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private gain: GainNode | null = null;
  private buffer: AudioBuffer | null = null;
  private src: AudioBufferSourceNode | null = null;
  /** Context time at which the current source plays its first sample. */
  private srcStartsAt = 0;
  private want: Want | null = null;
  private muted = false;
  /** Whether the song's clock should be running (the film plays, or we are in the menu). */
  private running = false;
  /** a resume() or suspend() of the context is in flight; sync() runs again when it settles */
  private syncing = false;

  /** `filmClock` returns the film's current time; used when the song is scheduled late. */
  constructor(private filmClock: () => number) {}

  private ensure(): AudioContext {
    if (!this.ctx) {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : 1;
      this.master.connect(this.ctx.destination);
      this.gain = this.ctx.createGain();
      this.gain.gain.value = 0;
      this.gain.connect(this.master);
      // the browser may also suspend or interrupt the context (Safari); always steer back
      this.ctx.addEventListener("statechange", () => this.sync());
    }
    return this.ctx;
  }

  /**
   * Call inside the visitor's key press or click: browsers only allow sound after one. This only
   * obtains permission; the clock runs only while `running` is wanted (see resume/suspend).
   */
  unlock(): void {
    const ctx = this.ensure();
    if (ctx.state === "running") return;
    // resume() must be called right here, inside the gesture; sync() then settles the wanted state
    this.syncing = true;
    void ctx
      .resume()
      .catch(() => undefined)
      .finally(() => {
        this.syncing = false;
        this.sync();
      });
  }

  /**
   * Bring the context to the wanted state. suspend() and resume() are asynchronous and can
   * overlap (the film's pause event, then arrival a moment later): calling the opposite one while
   * the first is still in flight used to be lost, leaving the music silent. Now only one runs at a
   * time, and when it settles the wanted state is checked again.
   */
  private sync(): void {
    const ctx = this.ctx;
    if (!ctx || this.syncing || ctx.state === "closed") return;
    const want = this.running ? "running" : "suspended";
    if (ctx.state === want) return;
    this.syncing = true;
    void (this.running ? ctx.resume() : ctx.suspend())
      .catch(() => undefined)
      .finally(() => {
        this.syncing = false;
        this.sync();
      });
  }

  /** Decode the downloaded file; if the song is already wanted, it is scheduled now. */
  async prepare(data: ArrayBuffer): Promise<void> {
    this.buffer = await this.ensure().decodeAudioData(data);
    this.apply();
  }

  get ready(): boolean {
    return !!this.buffer;
  }

  /** The journey has started: schedule the song against the film. */
  journey(): void {
    if (this.want?.mode === "menu") return;
    this.want = { mode: "journey" };
    this.apply();
  }

  /** Arrival: the song plays now and rises to the menu level. */
  menu(seconds: number): void {
    this.ensure(); // after close() (back from a game) a new context is made
    this.want = { mode: "menu", seconds };
    this.apply();
    this.resume();
  }

  /** Leaving for a game: fade the song out. */
  fadeOut(seconds: number): void {
    if (!this.ctx || !this.gain) return;
    const now = this.ctx.currentTime;
    const g = this.gain.gain;
    const v = g.value;
    g.cancelScheduledValues(now);
    g.setValueAtTime(v, now);
    g.linearRampToValueAtTime(0, now + seconds);
  }

  /** Leaving the page: stop the song and close the audio device. A later menu() reopens it. */
  close(): void {
    this.stopSource();
    const ctx = this.ctx;
    this.ctx = null;
    this.master = null;
    this.gain = null;
    this.running = false;
    void ctx?.close().catch(() => undefined);
    if (this.want?.mode === "menu") this.want = { mode: "menu", seconds: 1.5 };
  }

  /** Freeze the song and its fades (the film stopped, an error, or the notice is up). */
  suspend(): void {
    this.running = false;
    this.sync();
  }

  /** Let the song's clock run (the film is actually playing, or we are in the menu). */
  resume(): void {
    this.running = true;
    this.sync();
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (!this.ctx || !this.master) return;
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setTargetAtTime(m ? 0 : 1, now, 0.08);
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** Current music level (0-1, before mute), for the headless checks. */
  get level(): number {
    return this.gain?.gain.value ?? 0;
  }

  /** Number of live song sources (must never exceed 1), for the headless checks. */
  get sources(): number {
    return this.src ? 1 : 0;
  }

  get state(): string {
    return this.ctx?.state ?? "none";
  }

  /** For the checks: the overlap that used to lose the music (pause event, then arrival). */
  raceTest(): void {
    this.suspend();
    this.resume();
  }

  private apply(): void {
    const ctx = this.ctx;
    if (!ctx || !this.buffer || !this.gain || !this.want) return;
    const now = ctx.currentTime;
    const g = this.gain.gain;
    if (this.want.mode === "journey") {
      if (this.src) return;
      const t = Math.max(0, this.filmClock());
      const delay = Math.max(0, MUSIC_START - t);
      const offset = Math.max(0, t - MUSIC_START) % this.buffer.duration;
      this.startSource(now + delay, offset);
      g.cancelScheduledValues(now);
      g.setValueAtTime(levelAt(t), now);
      for (const [ct, v] of JOURNEY_CURVE) if (ct > t) g.linearRampToValueAtTime(v, now + (ct - t));
      return;
    }
    // menu: the song must be sounding now (a skip before MUSIC_START replaces the scheduled start)
    if (!this.src || this.srcStartsAt > now + 0.05) {
      this.stopSource();
      this.startSource(now, 0);
    }
    const v = g.value;
    g.cancelScheduledValues(now);
    g.setValueAtTime(v, now);
    g.linearRampToValueAtTime(LEVEL.normal, now + this.want.seconds);
  }

  private startSource(when: number, offset: number): void {
    const ctx = this.ctx!;
    const s = ctx.createBufferSource();
    s.buffer = this.buffer;
    s.loop = true;
    s.connect(this.gain!);
    s.start(when, offset);
    this.src = s;
    this.srcStartsAt = when;
  }

  private stopSource(): void {
    if (!this.src) return;
    try {
      this.src.stop();
    } catch {
      /* never started */
    }
    this.src.disconnect();
    this.src = null;
  }
}

function levelAt(t: number): number {
  const c = JOURNEY_CURVE;
  if (t <= c[0][0]) return c[0][1];
  for (let i = 1; i < c.length; i++) {
    const [t1, v1] = c[i];
    const [t0, v0] = c[i - 1];
    if (t <= t1) return v0 + ((v1 - v0) * (t - t0)) / (t1 - t0);
  }
  return c[c.length - 1][1];
}
